/**
 * 광고·분석 추적 공통 유틸 (2026-09-22)
 *
 * 관리자(SiteSettings '광고·분석 스크립트')에 입력한 ID 로 스크립트를 동적 로드하고,
 * 페이지뷰·전환 이벤트를 한 곳에서 보낸다. 목적: 광고 유입 → 상담 전화·문의 전환 측정.
 *
 * 규칙
 *  - 설정이 비었거나 '사용'이 꺼진 도구는 로드도, 이벤트 전송도 하지 않는다.
 *  - 같은 도구를 두 번 로드/초기화하지 않는다(모듈 상태 + 이미 페이지에 있는 스크립트 감지).
 *  - 이름·전화번호 등 개인정보는 이벤트 파라미터에 넣지 않는다.
 *  - 메타 이벤트(대소문자 정확히): 문의 저장 성공 → Lead, tel:·카톡 상담 클릭 → Contact.
 *    파라미터: content_name · content_ids · content_category('apartment'|'officetel') · contact_method.
 *    value/currency 는 넣지 않는다. Lead 는 서버 CAPI 와 같은 eventID 로 중복 제거.
 *  - fbclid: SPA 가 URL 을 바꾸기 전에 captureLandingParams() 가 _fbc 쿠키를 만들어 둔다.
 *  - 메타 픽셀 세부는 utils/metaPixel.ts. 이 파일이 모든 도구의 공통 진입점.
 */

import { initMetaPixel, metaContact, metaLead, metaPageView, META_PIXEL_ID_PATTERN, MetaContentParams } from './metaPixel';

export type ContentCategory = 'apartment' | 'officetel';
export type ContactMethod = 'form' | 'phone' | 'kakao';

export interface TrackingSettings {
  meta_pixel_enabled?: boolean;
  meta_pixel_id?: string | null;
  facebook_domain_verification?: string | null;
  ga4_enabled?: boolean;
  ga4_measurement_id?: string | null;
  google_ads_enabled?: boolean;
  google_ads_id?: string | null;
  google_ads_lead_label?: string | null;
  google_ads_call_label?: string | null;
  kakao_pixel_enabled?: boolean;
  kakao_pixel_id?: string | null;
  naver_wcs_enabled?: boolean;
  naver_wcs_id?: string | null;
  clickbaro_enabled?: boolean;
  clickbaro_script_url?: string | null;
}

/** 이벤트 대상 현장 (없으면 목록·지도 공통값) */
export interface TrackingProperty {
  id?: number | null;
  name: string;
  category?: ContentCategory;
}

export const LIST_CONTENT_NAME = '대구 할인분양 목록';
const UTM_STORAGE_KEY = 'vn_utm';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'] as const;
type UtmKey = typeof UTM_KEYS[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

// 백엔드 validators 와 같은 형식 — URL·스크립트 주입 방지를 위해 프론트에서도 한 번 더 확인
const ID_PATTERNS = {
  meta: META_PIXEL_ID_PATTERN,
  ga4: /^G-[A-Z0-9]{4,20}$/,
  ads: /^AW-\d{5,20}$/,
  label: /^[A-Za-z0-9_-]{4,50}$/,
  kakao: /^\d{5,25}$/,
  naver: /^[A-Za-z0-9]{6,30}$/,
  fbVerify: /^[A-Za-z0-9]+$/,
};

const SRC = {
  gtag: 'https://www.googletagmanager.com/gtag/js',
  kakao: 'https://t1.daumcdn.net/kas/static/kp.js',
  naver: 'https://wcs.pstatic.net/wcslog.js',
};

interface TrackerState {
  metaId: string | null;
  ga4Id: string | null;
  adsId: string | null;
  adsLeadLabel: string | null;
  adsCallLabel: string | null;
  kakaoId: string | null;
  naverId: string | null;
  clickbaroOn: boolean;
}

const emptyState = (): TrackerState => ({
  metaId: null, ga4Id: null, adsId: null, adsLeadLabel: null, adsCallLabel: null, kakaoId: null, naverId: null,
  clickbaroOn: false,
});

let state: TrackerState = emptyState();
let initialized = false;
let currentProperty: TrackingProperty | null = null;

type AnyWindow = Window & { [key: string]: any };
const w = (): AnyWindow => window as unknown as AnyWindow;

const pick = (enabled: boolean | undefined, value: string | null | undefined, pattern: RegExp): string | null => {
  if (enabled === false) return null;
  const v = (value || '').trim();
  return v && pattern.test(v) ? v : null;
};

// ─────────────────────────── 공통 헬퍼 ───────────────────────────

/** 브라우저 이벤트 ID (CAPI 중복 제거용 UUID) */
export function generateEventId(): string {
  const c: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function propertyCategory(propertyType?: string | null): ContentCategory {
  return propertyType === '오피스텔' ? 'officetel' : 'apartment';
}

const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
};

export function getMetaCookies(): { fbp: string | null; fbc: string | null } {
  return { fbp: getCookie('_fbp'), fbc: getCookie('_fbc') };
}

/** 첫 진입 URL 의 utm·fbclid 보존 — 라우터가 URL 을 바꾸기 전(App 모듈 로드 시) 1회 호출 */
export function captureLandingParams(loc: Location = window.location): void {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(loc.search);
  } catch {
    return;
  }

  // utm: 세션 첫 유입 기준(first-touch). 이미 저장돼 있으면 덮어쓰지 않는다.
  try {
    const utm: UtmParams = {};
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v && v.trim()) utm[k] = v.trim().slice(0, 100);
    });
    if (Object.keys(utm).length > 0 && !window.sessionStorage.getItem(UTM_STORAGE_KEY)) {
      window.sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
    }
  } catch {
    /* sessionStorage 차단(사파리 비공개 등) — 무시 */
  }

  // fbclid → _fbc (Meta 형식 fb.1.<ms>.<fbclid>). 픽셀이 늦게 init 돼도 클릭 ID 가 남는다.
  const fbclid = params.get('fbclid');
  if (fbclid && /^[A-Za-z0-9_-]+$/.test(fbclid)) {
    const existing = getCookie('_fbc');
    if (!existing || !existing.endsWith(`.${fbclid}`)) {
      const host = loc.hostname || '';
      const domain = /(^|\.)voonang\.com$/.test(host) ? '; domain=.voonang.com' : '';
      const maxAge = 90 * 24 * 60 * 60;
      document.cookie = `_fbc=fb.1.${Date.now()}.${fbclid}; path=/; max-age=${maxAge}; SameSite=Lax${domain}`;
    }
  }
}

export function getStoredUtm(): UtmParams {
  try {
    const raw = window.sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const out: UtmParams = {};
    UTM_KEYS.forEach((k) => {
      if (typeof parsed?.[k] === 'string' && parsed[k]) out[k] = String(parsed[k]).slice(0, 100);
    });
    return out;
  } catch {
    return {};
  }
}

/** 문의 폼 POST 에 함께 싣는 추적 필드 (개인정보 없음) */
export function buildLeadPayload(eventId: string): Record<string, string | null> {
  const { fbp, fbc } = getMetaCookies();
  return {
    meta_event_id: eventId,
    fbp,
    fbc,
    event_source_url: window.location.href,
    ...getStoredUtm(),
  };
}

/** 상세·지도 패널 등에서 현재 보고 있는 현장 (tel:·카톡 클릭 이벤트의 content_name/ids) */
export function setTrackingProperty(p: TrackingProperty | null): void {
  currentProperty = p;
}

export function getTrackingProperty(): TrackingProperty | null {
  return currentProperty;
}

// ─────────────────────────── 스크립트 로드 ───────────────────────────

/** 이 ID 가 우리 로더가 아닌 곳(관리자 HEAD/BODY 스크립트 등)에서 이미 쓰이고 있는지 */
const idAlreadyInPage = (id: string): boolean =>
  Array.from(document.scripts).some(
    (s) => !s.hasAttribute('data-vn-tracking') && ((s.src || '').includes(id) || (s.textContent || '').includes(id)),
  );

const findScript = (srcPart: string): HTMLScriptElement | null =>
  Array.from(document.scripts).find((s) => (s.src || '').includes(srcPart)) || null;

/** src 스크립트를 한 번만 넣는다. 이미 있으면 재사용. ready() 가 참이 되면 콜백 */
const loadScriptOnce = (src: string, matchPart: string, ready: () => boolean, onReady: () => void): void => {
  if (ready()) {
    onReady();
    return;
  }
  let el = findScript(matchPart);
  if (!el) {
    el = document.createElement('script');
    el.async = true;
    el.src = src;
    el.setAttribute('data-vn-tracking', 'true');
    document.head.appendChild(el);
  }
  let done = false;
  const finish = () => {
    if (done || !ready()) return;
    done = true;
    onReady();
  };
  el.addEventListener('load', finish);
  // 이미 로드가 끝난 기존 스크립트(load 이벤트가 지나간 경우) 대비 짧은 폴링
  let tries = 0;
  const timer = window.setInterval(() => {
    tries += 1;
    finish();
    if (done || tries > 100) window.clearInterval(timer);
  }, 100);
};

const ensureGtag = (firstId: string) => {
  const win = w();
  win.dataLayer = win.dataLayer || [];
  if (typeof win.gtag !== 'function') {
    win.gtag = function gtag() {
      // gtag 는 Arguments 객체를 그대로 넣어야 한다(배열 금지)
      // eslint-disable-next-line prefer-rest-params
      win.dataLayer.push(arguments);
    };
    win.gtag('js', new Date());
  }
  if (!findScript('googletagmanager.com/gtag/js')) {
    const s = document.createElement('script');
    s.async = true;
    s.src = `${SRC.gtag}?id=${encodeURIComponent(firstId)}`;
    s.setAttribute('data-vn-tracking', 'true');
    document.head.appendChild(s);
  }
};

const ga4PageView = (id: string) => {
  w().gtag('event', 'page_view', {
    send_to: id,
    page_location: window.location.href,
    page_path: window.location.pathname + window.location.search,
    page_title: document.title,
  });
};

const initGoogle = (ga4Id: string | null, adsId: string | null) => {
  const first = ga4Id || adsId;
  if (!first) return;
  ensureGtag(first);
  const gtag = w().gtag;
  if (ga4Id && !idAlreadyInPage(ga4Id)) {
    // 화면 이동 page_view 는 직접 보낸다(send_page_view:false 로 자동 전송과 이중 방지)
    gtag('config', ga4Id, { send_page_view: false });
    ga4PageView(ga4Id);
  }
  if (adsId && !idAlreadyInPage(adsId)) {
    gtag('config', adsId);
  }
};

const kakaoCall = (fn: (px: any) => void) => {
  const win = w();
  if (!state.kakaoId || typeof win.kakaoPixel !== 'function') return;
  try {
    fn(win.kakaoPixel(state.kakaoId));
  } catch {
    /* 무시 */
  }
};

const initKakao = () => {
  // 관리자 HEAD 스크립트의 인라인 kakaoPixel() 호출은 kp.js 비동기 로드 전에 실행돼 실패하므로
  // 여기서는 스크립트만 재사용하고 최초 pageView 는 직접 보낸다.
  loadScriptOnce(SRC.kakao, 't1.daumcdn.net/kas/static/kp.js', () => typeof w().kakaoPixel === 'function', () => {
    kakaoCall((px) => px.pageView());
  });
};

const naverDo = (arg?: Record<string, unknown>) => {
  const win = w();
  if (!state.naverId || typeof win.wcs_do !== 'function') return;
  win.wcs_add = win.wcs_add || {};
  win.wcs_add.wa = state.naverId;
  try {
    if (arg) win.wcs_do(arg);
    else win.wcs_do();
  } catch {
    /* 무시 */
  }
};

const initNaver = () => {
  const win = w();
  win.wcs_add = win.wcs_add || {};
  if (state.naverId) win.wcs_add.wa = state.naverId;
  loadScriptOnce(SRC.naver, 'wcs.pstatic.net/wcslog.js', () => typeof w().wcs_do === 'function', () => naverDo());
};

/**
 * clickbaro(텔레그램 방문·문의·전화 알림) 로더 1회 삽입.
 * track.js 가 clickbaro.com/api/track/script/{host} 를 넣고, 그 스크립트가 method=post 폼 submit·tel: 클릭을 감지한다.
 * 관리자 HEAD 스크립트에 같은 태그가 이미 있거나 clickbaro 가 이미 돌고 있으면(__CLICKBARO_TRACK_LOADED__) 넣지 않는다.
 * (최초 로드 1회는 이 스크립트가 보내고, SPA 화면 이동은 clickbaroSpaView() 가 직접 보낸다 — 아래)
 */
const loadClickbaro = (enabled: boolean | undefined, url: string | null | undefined) => {
  if (enabled === false) return;
  const src = (url || '').trim();
  if (!/^https:\/\/[^\s"'<>]+$/.test(src)) return;
  if (w().__CLICKBARO_TRACK_LOADED__) return;
  if (findScript(src) || findScript('clickbaro.com/api/track/script/')) return;
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  s.setAttribute('data-vn-tracking', 'true');
  document.head.appendChild(s);
};

/**
 * clickbaro SPA 화면 이동 재추적 (2026-09-23, telegramdev3 협의안)
 *
 * clickbaro 스크립트는 페이지 로드 1회만 방문을 보낸다. 우리 사이트는 클라이언트 라우팅이라
 * 첫 화면만 기록되던 문제 → 라우트가 바뀔 때마다 /api/track 에 직접 1건 보낸다.
 *
 * 규칙(협의)
 *  - visitorId 는 clickbaro 스크립트가 만든 값을 그대로 쓴다(쿠키 qlog_visitor_id → localStorage qlog_vid).
 *    값이 없으면(스크립트 로드 전) 그 이동은 건너뛴다. 임의 생성 금지 — 한 사람이 여러 명으로 갈라진다.
 *  - ⛔ 광고 파라미터(n_media·NaPm·utm_*·gclid 등)는 절대 다시 보내지 않는다(광고 클릭 중복 계상 방지).
 *  - 운영 도메인에서만 보낸다(로컬 개발이 운영 방문 기록에 섞인 9/22 사고 방지).
 *  - 같은 주소 연속 전송은 거른다(clickbaro 도 10초 내 중복은 무시).
 *  - 실패해도 화면 이동을 막지 않는다(try/catch + keepalive).
 */
const CLICKBARO_TRACK_URL = 'https://clickbaro.com/api/track';
const CLICKBARO_SITE = 'voonang.com';
const CLICKBARO_TITLE_STEP_MS = 300;   // 제목 변경 확인 간격
const CLICKBARO_TITLE_WAIT_MS = 3000;  // 제목이 안 바뀌어도 이만큼 뒤에는 보낸다
const AD_PARAM = /^(utm_|n_|na_|ad_)|^(NaPm|gclid|fbclid|wbraid|gbraid|msclkid|yclid|ref|referrer_code)$/i;
let clickbaroLastUrl = '';

const clickbaroVisitorId = (): string => {
  try {
    const m = document.cookie.match(/(?:^|;\s*)qlog_visitor_id=([^;]+)/);
    if (m) return decodeURIComponent(m[1]);
    return window.localStorage.getItem('qlog_vid') || '';
  } catch {
    return '';
  }
};

/** 현재 주소에서 광고 파라미터를 뺀 URL */
const clickbaroCleanUrl = (): string => {
  const { origin, pathname, search, hash } = window.location;
  let q = '';
  try {
    const p = new URLSearchParams(search);
    Array.from(p.keys()).forEach((k) => { if (AD_PARAM.test(k)) p.delete(k); });
    q = p.toString();
  } catch {
    q = '';
  }
  return `${origin}${pathname}${q ? `?${q}` : ''}${hash || ''}`;
};

let clickbaroTimer: ReturnType<typeof setTimeout> | undefined;

const clickbaroSend = (url: string, visitorId: string): void => {
  try {
    void fetch(CLICKBARO_TRACK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        site: CLICKBARO_SITE,
        url,
        referrer: document.referrer || '',
        visitorId,
        userAgent: navigator.userAgent,
        pageTitle: document.title || '',
      }),
      keepalive: true,
    }).catch(() => { /* 추적 실패는 무시 */ });
  } catch {
    /* 무시 */
  }
};

export function clickbaroSpaView(): void {
  if (!state.clickbaroOn || typeof window === 'undefined') return;
  const host = window.location.hostname;
  if (host !== CLICKBARO_SITE && !host.endsWith(`.${CLICKBARO_SITE}`)) return;
  const visitorId = clickbaroVisitorId();
  if (!visitorId) return;
  const url = clickbaroCleanUrl();
  if (url === clickbaroLastUrl) return;
  clickbaroLastUrl = url;

  // 화면 제목은 데이터를 받은 뒤(현장명 포함)에 바뀐다 — 바로 보내면 알림에 이전/기본 제목이 실린다.
  // (2026-09-24 대표 제보: 알림 제목이 '부냥 · 아파트 분양정보…' 로만 보임)
  // 제목이 바뀌거나 최대 CLICKBARO_TITLE_WAIT_MS 가 지나면 보낸다. 그 사이 다른 화면으로 옮겼으면 보내지 않는다
  // (그 화면이 자기 몫을 따로 보낸다).
  const prevTitle = document.title;
  if (clickbaroTimer) clearTimeout(clickbaroTimer);
  let waited = 0;
  const step = () => {
    waited += CLICKBARO_TITLE_STEP_MS;
    if (clickbaroCleanUrl() !== url) return;              // 다른 화면으로 이동 → 취소
    if (document.title !== prevTitle || waited >= CLICKBARO_TITLE_WAIT_MS) {
      clickbaroSend(url, visitorId);
      return;
    }
    clickbaroTimer = setTimeout(step, CLICKBARO_TITLE_STEP_MS);
  };
  clickbaroTimer = setTimeout(step, CLICKBARO_TITLE_STEP_MS);
}

/** 메타 도메인 인증 메타태그 (정적 index.html 에 같은 값이 있으면 생략) */
const applyDomainVerification = (value?: string | null) => {
  const v = (value || '').trim();
  if (!v || !ID_PATTERNS.fbVerify.test(v)) return;
  const exists = Array.from(document.querySelectorAll('meta[name="facebook-domain-verification"]'))
    .some((m) => m.getAttribute('content') === v);
  if (exists) return;
  const meta = document.createElement('meta');
  meta.setAttribute('name', 'facebook-domain-verification');
  meta.setAttribute('content', v);
  meta.setAttribute('data-vn-tracking', 'true');
  document.head.appendChild(meta);
};

/**
 * 설정값으로 추적 스크립트 로드 + 최초 페이지뷰. 앱 수명 동안 1회만 동작.
 * 관리자 HEAD/BODY 스크립트 주입(injectScripts) 뒤에 호출해야 중복 감지가 된다.
 */
export function initTracking(settings: TrackingSettings | null | undefined): void {
  if (initialized || !settings || typeof window === 'undefined') return;
  initialized = true;

  const adsId = pick(settings.google_ads_enabled, settings.google_ads_id, ID_PATTERNS.ads);
  state = {
    metaId: pick(settings.meta_pixel_enabled, settings.meta_pixel_id, ID_PATTERNS.meta),
    ga4Id: pick(settings.ga4_enabled, settings.ga4_measurement_id, ID_PATTERNS.ga4),
    adsId,
    adsLeadLabel: adsId ? pick(true, settings.google_ads_lead_label, ID_PATTERNS.label) : null,
    adsCallLabel: adsId ? pick(true, settings.google_ads_call_label, ID_PATTERNS.label) : null,
    kakaoId: pick(settings.kakao_pixel_enabled, settings.kakao_pixel_id, ID_PATTERNS.kakao),
    naverId: pick(settings.naver_wcs_enabled, settings.naver_wcs_id, ID_PATTERNS.naver),
    clickbaroOn: settings.clickbaro_enabled !== false,
  };

  applyDomainVerification(settings.facebook_domain_verification);
  try {
    if (state.metaId) initMetaPixel(state.metaId);
    initGoogle(state.ga4Id, state.adsId);
    if (state.kakaoId) initKakao();
    if (state.naverId) initNaver();
    loadClickbaro(settings.clickbaro_enabled, settings.clickbaro_script_url);
  } catch (e) {
    // 추적 실패가 화면을 깨뜨리면 안 된다
    console.warn('tracking init failed', e);
  }
}

/** SPA 라우트 변경 시 페이지뷰 (최초 페이지뷰는 initTracking 이 보냄) */
export function trackPageView(): void {
  const win = w();
  try {
    if (state.metaId) metaPageView();
    if (state.ga4Id && typeof win.gtag === 'function') ga4PageView(state.ga4Id);
    kakaoCall((px) => px.pageView());
    naverDo();
    clickbaroSpaView();
  } catch {
    /* 무시 */
  }
}

// ─────────────────────────── 전환 이벤트 ───────────────────────────

const contentParams = (ctx: TrackingProperty | null | undefined, method: ContactMethod): MetaContentParams => {
  const p = ctx || currentProperty;
  const params: MetaContentParams = {
    content_name: p?.name || LIST_CONTENT_NAME,
    content_category: p?.category || 'apartment',
    contact_method: method,
  };
  if (p?.id) params.content_ids = [String(p.id)];
  return params;
};

/** GA4 는 배열 파라미터를 못 받으므로 content_id 단일 문자열로 */
const ga4Params = (params: MetaContentParams, eventId: string) => {
  const { content_ids: ids, ...rest } = params;
  const out: Record<string, unknown> = { ...rest, event_id: eventId };
  if (Array.isArray(ids) && ids.length) out.content_id = ids[0];
  return out;
};

/**
 * 문의 폼 서버 저장 성공 응답 뒤 호출. eventId 는 폼 POST 에 실어 보낸 것과 같은 값
 * (서버 CAPI Lead 와 중복 제거). 반환값 = 사용한 eventId.
 */
export function trackLead(ctx?: TrackingProperty | null, eventId: string = generateEventId()): string {
  const win = w();
  const params = contentParams(ctx, 'form');
  try {
    if (state.metaId) metaLead(params, eventId);
    if (typeof win.gtag === 'function') {
      if (state.ga4Id) win.gtag('event', 'generate_lead', { send_to: state.ga4Id, ...ga4Params(params, eventId) });
      if (state.adsId && state.adsLeadLabel) {
        win.gtag('event', 'conversion', { send_to: `${state.adsId}/${state.adsLeadLabel}`, transaction_id: eventId });
      }
    }
    kakaoCall((px) => px.participation());
    // 네이버 전환: 4 = 신청/예약
    const wcs = win.wcs;
    if (state.naverId && wcs && typeof wcs.cnv === 'function') naverDo({ cnv: wcs.cnv('4', '1') });
  } catch {
    /* 무시 */
  }
  return eventId;
}

/** 전화(tel:)·카톡 상담 클릭 */
export function trackContact(method: 'phone' | 'kakao', ctx?: TrackingProperty | null): string {
  const win = w();
  const eventId = generateEventId();
  const params = contentParams(ctx, method);
  try {
    if (state.metaId) metaContact(params, eventId);
    if (typeof win.gtag === 'function') {
      if (state.ga4Id) win.gtag('event', 'contact', { send_to: state.ga4Id, ...ga4Params(params, eventId) });
      if (state.adsId && state.adsCallLabel) {
        win.gtag('event', 'conversion', { send_to: `${state.adsId}/${state.adsCallLabel}`, transaction_id: eventId });
      }
    }
  } catch {
    /* 무시 */
  }
  return eventId;
}

/** 문서 전체 tel:·pf.kakao.com 링크 클릭 감지 (캡처 단계 — 개별 onClick 의 preventDefault 와 무관) */
export function installContactClickTracking(): () => void {
  const handler = (e: MouseEvent) => {
    const target = e.target as Element | null;
    const a = target && typeof target.closest === 'function' ? target.closest('a[href]') : null;
    if (!a) return;
    const href = (a.getAttribute('href') || '').trim();
    if (/^tel:/i.test(href)) trackContact('phone');
    else if (/^https?:\/\/pf\.kakao\.com\//i.test(href)) trackContact('kakao');
  };
  document.addEventListener('click', handler, true);
  return () => document.removeEventListener('click', handler, true);
}

/** 테스트 전용 */
export function __resetTrackingForTests(): void {
  state = emptyState();
  initialized = false;
  currentProperty = null;
  clickbaroLastUrl = '';
  if (clickbaroTimer) { clearTimeout(clickbaroTimer); clickbaroTimer = undefined; }
}
